import { GetJarAdsSDK } from './core';
import { HostBootstrap } from './host';
// SDK Version (auto-generated at build time)
const SDK_VERSION = __BUILD_VERSION__;
const BUILD_TIMESTAMP = __BUILD_TIMESTAMP__;
const BUILD_NUMBER = __BUILD_NUMBER__;
// Create singleton instance
const sdkInstance = new GetJarAdsSDK();
// Host bootstrap is created lazily on first initHost() call. A top-frame
// page that only embeds games (never calls initHost) must not become a
// host — keeping the construction lazy enforces that invariant. The
// renderer side effects (IMA SDK load, GPT script injection) all live
// inside HostBootstrap.start(), so game-only consumers pay zero runtime
// cost beyond a few hundred bytes of class declarations in the bundle.
let hostBootstrap = null;
// Games can be pre-registered before initHost() resolves; we keep a small
// queue here so the registration order doesn't matter. Drained when the
// bootstrap is constructed.
const pendingHostRegistrations = [];
function getOrCreateHostBootstrap() {
    if (!hostBootstrap) {
        hostBootstrap = new HostBootstrap();
        for (const r of pendingHostRegistrations) {
            hostBootstrap.registerGame(r);
        }
        pendingHostRegistrations.length = 0;
    }
    return hostBootstrap;
}
/**
 * GetJarAds - Public API
 * Browser-based Ads SDK for HTML5 games
 */
const GetJarAds = {
    /**
     * SDK version (semantic: 1.0.4.0001)
     */
    version: SDK_VERSION,
    /**
     * Build number (0001, 0002, etc.)
     */
    buildNumber: BUILD_NUMBER,
    /**
     * Build timestamp (ISO 8601 format)
     */
    buildTime: BUILD_TIMESTAMP,
    /**
     * Initialize the SDK
     * Must be called before any other SDK methods
     */
    init: (config) => {
        console.log(`[GetJarAds] SDK v${SDK_VERSION} (build ${BUILD_NUMBER}, ${BUILD_TIMESTAMP})`);
        return sdkInstance.init(config);
    },
    /**
     * Show a banner ad — preferred name.
     * @param config Banner configuration
     */
    showBanner: (config) => {
        return sdkInstance.createBanner(config);
    },
    /**
     * @deprecated Use `showBanner`. Kept for backwards compatibility.
     */
    createBanner: (config) => {
        return sdkInstance.createBanner(config);
    },
    /**
     * Render a page-surround banner/MREC on the getjar.com parent page,
     * attributed to the currently-embedded game's developer (ADR-001 Part A).
     * Requires `gameId` and `publisherId` on the config — they identify
     * which embedded game this ON_SITE impression rolls up to. Same
     * inventory product as `showBanner`; only the `source` targeting key
     * differs (`on_site` vs `sdk`).
     */
    showOnPageBanner: (config) => {
        return sdkInstance.showOnPageBanner(config);
    },
    /**
     * Show a display (out-of-page) interstitial ad — preferred name.
     * @param config Interstitial configuration
     */
    showInterstitial: (config) => {
        return sdkInstance.createInterstitial(config);
    },
    /**
     * @deprecated Use `showInterstitial`. Kept for backwards compatibility.
     */
    createInterstitial: (config) => {
        return sdkInstance.createInterstitial(config);
    },
    /**
     * Show a GPT display-rewarded ad.
     * @deprecated GPT display-rewarded isn't provisioned in the current GAM setup.
     * Use `showRewardedVideo()` instead.
     */
    showRewarded: (config) => {
        return sdkInstance.showRewarded(config);
    },
    /**
     * Show a non-rewarded video interstitial (IMA).
     * Uses `/getjar/games_video_interstitial_non_rewarded`.
     */
    showVideoInterstitial: (config) => {
        return sdkInstance.showVideoInterstitial(config);
    },
    /**
     * @deprecated Use `showVideoInterstitial`. Kept for backwards compatibility.
     */
    showInterstitialVideo: (config) => {
        return sdkInstance.showInterstitialVideo(config);
    },
    /**
     * Show a rewarded video ad (IMA).
     * Uses `/getjar/games_video_interstitial`.
     */
    showRewardedVideo: (config) => {
        return sdkInstance.showRewardedVideo(config);
    },
    /**
     * Check if SDK is initialized and ready
     */
    isReady: () => {
        return sdkInstance.isReady();
    },
    /**
     * Get current session ID (for debugging)
     */
    getSessionId: () => {
        return sdkInstance.getSessionId();
    },
    /**
     * Get GAM network ID (for building VAST tags)
     * @deprecated This method exposes GAM implementation details and will be removed in a future version.
     * Video ads are now configured server-side. Use showRewardedVideo() and showInterstitialVideo() without VAST URL construction.
     */
    getNetworkId: () => {
        return sdkInstance.getNetworkId();
    },
    /**
     * Get the current ad context
     * @returns 'IN_GAME' (iframe) or 'ON_SITE' (direct)
     */
    getAdContext: () => {
        return sdkInstance.getAdContext();
    },
    /**
     * Preload interstitial ad SDKs for faster display
     */
    preloadInterstitial: () => {
        return sdkInstance.preloadInterstitial();
    },
    /**
     * Preload rewarded ad SDKs for faster display
     */
    preloadRewarded: () => {
        return sdkInstance.preloadRewarded();
    },
    /**
     * Check if interstitial is preloaded and ready
     */
    isInterstitialReady: () => {
        return sdkInstance.isInterstitialReady();
    },
    /**
     * Check if rewarded is preloaded and ready
     */
    isRewardedReady: () => {
        return sdkInstance.isRewardedReady();
    },
    /**
     * Get preload error (if any)
     */
    getPreloadError: (type) => {
        return sdkInstance.getPreloadError(type);
    },
    /**
     * Re-initialize the SDK after a failure
     */
    reinit: () => {
        return sdkInstance.reinit();
    },
    /**
     * Host role — initialise the parent-page postMessage bridge so game
     * iframes can route full-screen ads (interstitial / video interstitial /
     * rewarded video) here for host-side rendering per ADR-001.
     *
     * Call from the getjar.com parent page only. A top-frame page that does
     * NOT embed games must not call this — the host shell adds a global
     * `message` listener that ACKs handshakes and would respond to any
     * inbound bridge envelope from an allow-listed origin.
     *
     * Idempotent: a second call with the same (or a different) config
     * returns the in-flight promise from the first call. The first call
     * wins; reconfiguration requires a `GetJarAds.stopHost()` first
     * (currently not exposed — host lifecycles span the page lifetime).
     *
     * Lazy by design: this is what triggers loading the IMA SDK, the GPT
     * script, and constructing the renderers. Games that only call init()
     * pay nothing for this code path.
     */
    initHost: (config) => {
        return getOrCreateHostBootstrap().start(config);
    },
    /**
     * Register the trusted attribution for a game iframe the host has
     * embedded. The host trusts THIS registry — never the iframe's
     * self-claim in the handshake payload. Safe to call before initHost();
     * registrations are queued and applied once the bootstrap is up.
     */
    registerGame: (input) => {
        if (hostBootstrap) {
            hostBootstrap.registerGame(input);
        }
        else {
            pendingHostRegistrations.push(input);
        }
    },
    /**
     * Tear down the host bridge (detaches the postMessage listener, clears
     * the trusted-game registry). Idempotent. Useful for SPA teardown / hot
     * reload, and required between tests that exercise the public
     * `initHost()` surface — without it, the bootstrap's "start once"
     * idempotency would pin the listener to the first test's window.
     *
     * Has no effect on the game-side `init()` / `sdkInstance` state.
     */
    stopHost: () => {
        if (hostBootstrap) {
            hostBootstrap.stop();
            hostBootstrap = null;
        }
        pendingHostRegistrations.length = 0;
    },
};
// Default export for UMD build
export default GetJarAds;
